import {
  GUEST_FREEMIUM_CARRYOVER_APPLIED_KEY,
  GUEST_USAGE_TODAY_KEY,
  getDateScopedUsageSeconds,
  getRegisteredUsageSeconds,
  getTodayKeyDate,
  setRegisteredUsageSeconds,
  wasUserCreatedToday,
} from "@/lib/livekit-agent-usage-storage";

type FreemiumCarryoverLogger = {
  warn: (message: string, data?: unknown) => void;
  error: (message: string, data?: unknown) => void;
};

type PostHogLike = {
  capture: (eventName: string, properties?: Record<string, unknown>) => void;
};

type FreemiumCarryoverUser = {
  id?: string | null;
  email?: string | null;
  created_at?: string | null;
};

export type ApplyGuestUsageToFreemiumDayInput = {
  user?: FreemiumCarryoverUser | null;
  maxGuestSeconds: number;
  maxRegisteredSecondsPerDay: number;
  posthogClient?: PostHogLike | null;
  hasPosthogKey: boolean;
  logger: FreemiumCarryoverLogger;
};

export type ApplyGuestUsageToFreemiumDayResult =
  | {
      applied: false;
      reason: "missing_user" | "existing_user" | "no_guest_usage" | "already_applied";
    }
  | {
      applied: true;
      guestSecondsToday: number;
      secondsApplied: number;
      freemiumSecondsUsedToday: number;
      freemiumSecondsRemainingToday: number;
    };

export function applyGuestUsageToFreemiumDayIfNeeded({
  user,
  maxGuestSeconds,
  maxRegisteredSecondsPerDay,
  posthogClient,
  hasPosthogKey,
  logger,
}: ApplyGuestUsageToFreemiumDayInput): ApplyGuestUsageToFreemiumDayResult {
  if (!user?.id) {
    return { applied: false, reason: "missing_user" };
  }

  if (!wasUserCreatedToday(user.created_at || undefined)) {
    return { applied: false, reason: "existing_user" };
  }

  const today = getTodayKeyDate();
  const guestSecondsToday = Math.min(
    getDateScopedUsageSeconds(GUEST_USAGE_TODAY_KEY, logger),
    maxGuestSeconds
  );
  if (guestSecondsToday <= 0) {
    return { applied: false, reason: "no_guest_usage" };
  }

  let alreadyApplied = 0;
  try {
    const raw = localStorage.getItem(GUEST_FREEMIUM_CARRYOVER_APPLIED_KEY);
    if (raw) {
      const marker = JSON.parse(raw) as {
        userId?: unknown;
        date?: unknown;
        secondsApplied?: unknown;
      };
      if (
        marker?.userId === user.id &&
        marker.date === today &&
        typeof marker.secondsApplied === "number" &&
        Number.isFinite(marker.secondsApplied)
      ) {
        alreadyApplied = Math.max(0, marker.secondsApplied);
      }
    }
  } catch {}

  const secondsToApply = Math.max(0, guestSecondsToday - alreadyApplied);
  if (secondsToApply <= 0) {
    return { applied: false, reason: "already_applied" };
  }

  const usageIdentity = user.email || user.id;
  const usedBefore = getRegisteredUsageSeconds(usageIdentity, logger);
  const usedAfter = Math.min(maxRegisteredSecondsPerDay, usedBefore + secondsToApply);
  setRegisteredUsageSeconds(usageIdentity, usedAfter);

  try {
    localStorage.setItem(
      GUEST_FREEMIUM_CARRYOVER_APPLIED_KEY,
      JSON.stringify({
        userId: user.id,
        date: today,
        secondsApplied: guestSecondsToday,
      })
    );
  } catch {}

  const result = {
    applied: true,
    guestSecondsToday,
    secondsApplied: usedAfter - usedBefore,
    freemiumSecondsUsedToday: usedAfter,
    freemiumSecondsRemainingToday: Math.max(0, maxRegisteredSecondsPerDay - usedAfter),
  } as const;

  if (posthogClient && hasPosthogKey) {
    posthogClient.capture("fullscreen_guest_minutes_carried_to_freemium", {
      user_id: user.id,
      guest_seconds_today: result.guestSecondsToday,
      seconds_applied: result.secondsApplied,
      freemium_seconds_used_today: result.freemiumSecondsUsedToday,
      freemium_seconds_remaining_today: result.freemiumSecondsRemainingToday,
    });
  }

  return result;
}
